import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Grid, Button, Chip, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Snackbar, CircularProgress, Divider, Table, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Checkbox, InputAdornment, FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation } from '@apollo/client';
import {
  ALL_SERVICE_WISHLISTS_QUERY,
  UPDATE_SERVICE_WISHLIST_MUTATION,
  APPROVE_SERVICE_WISHLIST_MUTATION,
  REJECT_SERVICE_WISHLIST_MUTATION,
  DELETE_SERVICE_WISHLIST_MUTATION,
  ADMIN_CREATE_SERVICE_WISHLIST_MUTATION,
  USERS_QUERY,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const ALL_STATUSES = [
  'pending_approval', '', 'pending_confirmation', 'approved', 'contracted', 'rejected', 'draft', 'inactive',
];

const EMPTY_EDIT = {
  forWhom: '', groomClientId: '', totalBudget: '', eventDate: '', eventScale: '', guestCount: '', notes: '', status: '',
};

const STATUS_COLORS = {
  draft: 'default',
  pending_confirmation: 'warning',
  pending_approval: 'info',
  approved: 'success',
  contracted: 'success',
  rejected: 'error',
};

export default function AdminWishlistApprovalPage() {
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // ids: one wishlist, or several when rejecting a selection
  const [rejectDialog, setRejectDialog] = useState({ open: false, ids: [] });
  const [rejectReason, setRejectReason] = useState('');
  const [detailDialog, setDetailDialog] = useState({ open: false, wishlist: null });
  const [editDialog,   setEditDialog]   = useState({ open: false, wishlist: null });
  const [editForm,     setEditForm]     = useState(EMPTY_EDIT);
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm,   setCreateForm]   = useState({ clientId: '', groomClientId: '', forWhom: 'bride', totalBudget: '', eventDate: '', eventScale: '', guestCount: '', notes: '' });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // Load every wishlist once; status, search and counts are worked out in the browser
  const { data, loading, refetch } = useQuery(ALL_SERVICE_WISHLISTS_QUERY, {
    variables: { status: null },
    fetchPolicy: 'cache-and-network',
  });
  const { data: usersData } = useQuery(USERS_QUERY);

  const [updateWishlist, { loading: updating }] = useMutation(UPDATE_SERVICE_WISHLIST_MUTATION, {
    onCompleted: () => { refetch(); setEditDialog({ open: false, wishlist: null }); showSnack('Wishlist updated.'); },
    onError: e => showSnack(e.message, 'error'),
  });

  const [approveWishlist, { loading: approving }] = useMutation(APPROVE_SERVICE_WISHLIST_MUTATION, {
    onCompleted: () => { refetch(); showSnack('Wishlist approved! Contract has been created.'); },
    onError: e => showSnack(e.message, 'error'),
  });

  // Plain versions (no per-call refresh/snackbar) for approving/rejecting several at once
  const [approveOne] = useMutation(APPROVE_SERVICE_WISHLIST_MUTATION);
  const [rejectOne] = useMutation(REJECT_SERVICE_WISHLIST_MUTATION);
  const [rejectWishlist] = useMutation(REJECT_SERVICE_WISHLIST_MUTATION, {
    onCompleted: () => { refetch(); setRejectDialog({ open: false, ids: [] }); setRejectReason(''); showSnack('Wishlist rejected.'); },
    onError: e => showSnack(e.message, 'error'),
  });

  const [deleteWishlist] = useMutation(DELETE_SERVICE_WISHLIST_MUTATION, {
    onCompleted: () => { refetch(); showSnack('Wishlist deleted.'); },
    onError: e => showSnack(e.message, 'error'),
  });
  const [createWishlist, { loading: creating }] = useMutation(ADMIN_CREATE_SERVICE_WISHLIST_MUTATION, {
    onCompleted: () => {
      refetch();
      setCreateDialog(false);
      setCreateForm({ clientId: '', groomClientId: '', forWhom: 'bride', totalBudget: '', eventDate: '', eventScale: '', guestCount: '', notes: '' });
      showSnack('Service wizard created.');
    },
    onError: e => showSnack(e.message, 'error'),
  });

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to permanently delete this wishlist?')) {
      deleteWishlist({ variables: { id } });
    }
  };

  const allWishlists = data?.allServiceWishlists || [];
  const users = (usersData?.users || []).filter(u => ['bride_family', 'groom_family', 'matchmaker'].includes(u.role?.name));

  const handleApprove = (id) => {
    approveWishlist({ variables: { id } });
  };

  const handleReject = async () => {
    const { ids } = rejectDialog;
    if (ids.length === 1) {
      rejectWishlist({ variables: { id: ids[0], reason: rejectReason || null } });
      return;
    }
    await runBulk(ids, id => rejectOne({ variables: { id, reason: rejectReason || null } }), 'rejected');
    setRejectDialog({ open: false, ids: [] });
    setRejectReason('');
  };

  // Approve/reject several wishlists; reports how many succeeded and why any failed
  const runBulk = async (ids, action, verb) => {
    setBulkBusy(true);
    const failures = [];
    for (const id of ids) {
      try { await action(id); } catch (e) { failures.push(e.message); }
    }
    setBulkBusy(false);
    setSelected(new Set());
    await refetch();
    const done = ids.length - failures.length;
    if (failures.length) showSnack(`${done} ${verb}, ${failures.length} failed: ${[...new Set(failures)].join('; ')}`, 'warning');
    else showSnack(`${done} wishlist(s) ${verb}.${verb === 'approved' ? ' Contracts have been created.' : ''}`);
  };

  const openEdit = (wishlist) => {
    setEditForm({
      forWhom: wishlist.forWhom || 'bride', groomClientId: wishlist.groomUserId || '', totalBudget: wishlist.totalBudget ?? '', eventDate: wishlist.eventDate || '',
      eventScale: wishlist.eventScale || '', guestCount: wishlist.guestCount ?? '', notes: wishlist.notes || '', status: wishlist.status || 'draft',
    });
    setEditDialog({ open: true, wishlist });
  };

  const handleEditSave = () => {
    const { wishlist } = editDialog;
    if (!wishlist) return;
    if (editForm.forWhom === 'both' && !editForm.groomClientId) {
      return showSnack('Select a groom for this joint wishlist.', 'error');
    }
    updateWishlist({ variables: { input: {
      id: wishlist.id, forWhom: editForm.forWhom, status: editForm.status,
      groomClientId: editForm.forWhom === 'both' ? editForm.groomClientId : null,
      totalBudget: editForm.totalBudget === '' ? null : Number(editForm.totalBudget), eventDate: editForm.eventDate || null,
      eventScale: editForm.eventScale || null, guestCount: editForm.guestCount === '' ? null : Number(editForm.guestCount), notes: editForm.notes || null,
    } } });
  };

  const handleInactivate = (wishlist) => updateWishlist({ variables: { input: { id: wishlist.id, status: 'inactive' } } });
  const handleReactivate = (wishlist) => updateWishlist({ variables: { input: { id: wishlist.id, status: 'draft' } } });

  const handleCreate = () => {
    if (!createForm.clientId) return showSnack('Select the bride, groom, or matchmaker first.', 'error');
    if (createForm.forWhom === 'both' && !createForm.groomClientId) {
      return showSnack('Select both a bride and a groom for a joint wishlist.', 'error');
    }
    createWishlist({ variables: { input: {
      clientId: createForm.clientId,
      groomClientId: createForm.forWhom === 'both' ? createForm.groomClientId : null,
      forWhom: createForm.forWhom, serviceSelections: [],
      totalBudget: createForm.totalBudget === '' ? null : Number(createForm.totalBudget), eventDate: createForm.eventDate || null,
      eventScale: createForm.eventScale || null, guestCount: createForm.guestCount === '' ? null : Number(createForm.guestCount), notes: createForm.notes || null,
    } } });
  };

  const getUserName = (wl) => {
    if (wl.user?.profile?.firstName) return `${wl.user.profile.firstName} ${wl.user.profile.lastName || ''}`.trim();
    return wl.user?.email || 'Unknown';
  };

  const getGroomName = (wl) => {
    if (!wl.groomUser) return null;
    if (wl.groomUser?.profile?.firstName) return `${wl.groomUser.profile.firstName} ${wl.groomUser.profile.lastName || ''}`.trim();
    return wl.groomUser?.email || null;
  };

  const getContractorName = (wl) => {
    if (!wl.contractor) return '—';
    if (wl.contractor?.profile?.firstName) return `${wl.contractor.profile.firstName} ${wl.contractor.profile.lastName || ''}`.trim();
    return wl.contractor?.email || '—';
  };

  // Text a wishlist can be found by: people (names & emails), services, status, date, notes
  const searchText = (wl) => [
    getUserName(wl), wl.user?.email, getGroomName(wl), wl.groomUser?.email,
    wl.contractor ? getContractorName(wl) : '', wl.contractor?.email,
    wl.forWhom === 'both' ? 'bride groom both' : wl.forWhom,
    wl.status.replace(/_/g, ' '), wl.eventDate, wl.notes,
    ...(wl.serviceSelections || []).flatMap(c => [c.categoryName, c.category, ...(c.services || []).filter(x => x.selected).map(x => x.name)]),
  ].filter(Boolean).join(' ').toLowerCase();

  // Every search word must appear somewhere in the wishlist's searchable text
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searched = allWishlists.filter(wl => {
    const text = searchText(wl);
    return words.every(w => text.includes(w));
  });
  const statusCount = (st) => (st ? searched.filter(w => w.status === st).length : searched.length);
  const wishlists = statusFilter ? searched.filter(w => w.status === statusFilter) : searched;
  const selectable = wishlists.filter(w => w.status === 'pending_approval');
  const selectedPending = selectable.filter(w => selected.has(w.id));
  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Wishlist Approvals</Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve service wishlists submitted by families and contractors
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateDialog(true)}>Add Service Wizard</Button>
      </Stack>

      {/* Search, status and bulk actions */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack spacing={1.5}>
          <TextField
            size="small" fullWidth value={search} onChange={e => { setSearch(e.target.value); setSelected(new Set()); }}
            placeholder="Search by family, groom or matchmaker name / email, service, status, event date or notes"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" fontWeight={600}>Status:</Typography>
            {ALL_STATUSES.map(st => (
              <Chip
                key={st || 'all'}
                label={`${st ? st.replace(/_/g, ' ') : 'All'} (${statusCount(st)})`}
                onClick={() => { setStatusFilter(st); setSelected(new Set()); }}
                color={statusFilter === st ? 'primary' : 'default'}
                variant={statusFilter === st ? 'filled' : 'outlined'}
                sx={{ textTransform: 'capitalize' }}
              />
            ))}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            {selectable.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedPending.length === selectable.length}
                    indeterminate={selectedPending.length > 0 && selectedPending.length < selectable.length}
                    onChange={e => setSelected(e.target.checked ? new Set(selectable.map(w => w.id)) : new Set())}
                  />
                }
                label={`Select all ${selectable.length} awaiting approval`}
              />
            )}
            {selectedPending.length > 0 && (
              <>
                <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled={bulkBusy}
                  onClick={() => runBulk(selectedPending.map(w => w.id), id => approveOne({ variables: { id } }), 'approved')}>
                  Approve selected ({selectedPending.length})
                </Button>
                <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} disabled={bulkBusy}
                  onClick={() => setRejectDialog({ open: true, ids: selectedPending.map(w => w.id) })}>
                  Reject selected ({selectedPending.length})
                </Button>
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
              Showing {wishlists.length} of {allWishlists.length}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      {!loading && wishlists.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '2px dashed #e0e0e0' }}>
          <Typography color="text.secondary">
            {search ? 'No wishlists match your search.' : 'No wishlists found with this status.'}
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Paged items={wishlists}>{rows => rows.map(wl => (
          <Grid item xs={12} md={6} lg={4} key={wl.id}>
            <Card elevation={wl.status === 'pending_approval' ? 4 : 2} sx={{
              borderRadius: 3,
              border: wl.status === 'pending_approval' ? '2px solid #1976d2' : 'none',
              opacity: wl.status === 'inactive' ? 0.65 : 1,
            }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  {wl.status === 'pending_approval' && (
                    <Checkbox size="small" sx={{ ml: -1 }} checked={selected.has(wl.id)} onChange={() => toggle(wl.id)}
                      inputProps={{ 'aria-label': `Select wishlist for ${getUserName(wl)}` }} />
                  )}
                  <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                    {wl.forWhom === 'both' ? 'Bride & Groom' : wl.forWhom === 'bride' ? 'Bride Family' : 'Groom Family'}
                  </Typography>
                  <Chip label={wl.status.replace(/_/g, ' ')} color={STATUS_COLORS[wl.status] || 'default'} size="small" />
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  {wl.forWhom === 'both' ? 'Bride' : 'Client'}: <strong>{getUserName(wl)}</strong>
                  {wl.user?.email && getUserName(wl) !== wl.user.email ? ` · ${wl.user.email}` : ''}
                </Typography>
                {wl.forWhom === 'both' && (
                  <Typography variant="body2" color="text.secondary">
                    Groom: <strong>{getGroomName(wl) || 'Not set'}</strong>
                  </Typography>
                )}
                {wl.contractor && (
                  <Typography variant="body2" color="text.secondary">
                    Contractor: <strong>{getContractorName(wl)}</strong>
                  </Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" mb={0.5}>
                  <strong>{(wl.serviceSelections || []).length}</strong> service categories
                  {wl.totalBudget ? ` · ₹${Number(wl.totalBudget).toLocaleString()}` : ''}
                </Typography>
                {wl.eventDate && (
                  <Typography variant="body2" color="text.secondary">
                    Date: {wl.eventDate}{wl.guestCount ? ` · ${wl.guestCount} guests` : ''}
                  </Typography>
                )}

                <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={1} mb={1}>
                  {(wl.serviceSelections || []).slice(0, 5).map((cat, i) => (
                    <Chip key={i} label={cat.categoryName || cat.category} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                  ))}
                  {(wl.serviceSelections || []).length > 5 && (
                    <Chip label={`+${(wl.serviceSelections || []).length - 5} more`} size="small" />
                  )}
                </Stack>

                {wl.rejectionReason && (
                  <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                    {wl.rejectionReason}
                  </Alert>
                )}

                <Stack direction="row" spacing={0.5} mt={2} flexWrap="wrap">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => setDetailDialog({ open: true, wishlist: wl })}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => openEdit(wl)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {wl.status === 'pending_approval' && (
                    <>
                      <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                        onClick={() => handleApprove(wl.id)} disabled={approving}>
                        Approve
                      </Button>
                      <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                        onClick={() => setRejectDialog({ open: true, ids: [wl.id] })}>
                        Reject
                      </Button>
                    </>
                  )}
                  {wl.status === 'pending_confirmation' && (
                    <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                      onClick={() => setRejectDialog({ open: true, ids: [wl.id] })}>
                      Reject
                    </Button>
                  )}
                  {!['inactive', 'contracted'].includes(wl.status) && (
                    <Tooltip title="Inactivate">
                      <IconButton size="small" color="warning" onClick={() => handleInactivate(wl)}>
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {['inactive', 'rejected'].includes(wl.status) && (
                    <Tooltip title="Restore to Draft">
                      <IconButton size="small" color="info" onClick={() => handleReactivate(wl)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(wl.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {wl.contractId && (
                    <Chip label="Contract" icon={<DescriptionIcon />} size="small" color="success" variant="outlined" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}</Paged>
      </Grid>

      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Service Wizard</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <FormControl fullWidth>
              <InputLabel>For Whom</InputLabel>
              <Select value={createForm.forWhom} label="For Whom" onChange={e => setCreateForm(f => ({ ...f, forWhom: e.target.value, clientId: '', groomClientId: '' }))}>
                <MenuItem value="bride">Bride Family</MenuItem>
                <MenuItem value="groom">Groom Family</MenuItem>
                <MenuItem value="both">Both Families</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{createForm.forWhom === 'both' ? 'For Bride' : 'For user'}</InputLabel>
              <Select value={createForm.clientId} label={createForm.forWhom === 'both' ? 'For Bride' : 'For user'}
                onChange={e => setCreateForm(f => ({ ...f, clientId: e.target.value }))}>
                {(createForm.forWhom === 'both' ? users.filter(u => u.role?.name === 'bride_family') : users)
                  .map(u => <MenuItem key={u.id} value={u.id}>{u.email} ({u.role?.displayName || u.role?.name})</MenuItem>)}
              </Select>
            </FormControl>
            {createForm.forWhom === 'both' && (
              <FormControl fullWidth required>
                <InputLabel>For Groom</InputLabel>
                <Select value={createForm.groomClientId} label="For Groom"
                  onChange={e => setCreateForm(f => ({ ...f, groomClientId: e.target.value }))}>
                  {users.filter(u => u.role?.name === 'groom_family').map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.email} ({u.role?.displayName || u.role?.name})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField label="Total Budget (₹)" type="number" value={createForm.totalBudget} onChange={e => setCreateForm(f => ({ ...f, totalBudget: e.target.value }))} />
            <TextField label="Event Date" type="date" InputLabelProps={{ shrink: true }} value={createForm.eventDate} onChange={e => setCreateForm(f => ({ ...f, eventDate: e.target.value }))} />
            <TextField label="Notes" multiline rows={3} value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ────────────────────────────────── */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, wishlist: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Wishlist</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <FormControl fullWidth>
              <InputLabel>For Whom</InputLabel>
              <Select value={editForm.forWhom} label="For Whom" onChange={e => setEditForm(f => ({ ...f, forWhom: e.target.value }))}>
                <MenuItem value="bride">Bride Family</MenuItem>
                <MenuItem value="groom">Groom Family</MenuItem>
                <MenuItem value="both">Both Families</MenuItem>
              </Select>
            </FormControl>
            {editForm.forWhom === 'both' && (
              <FormControl fullWidth required>
                <InputLabel>For Groom</InputLabel>
                <Select value={editForm.groomClientId} label="For Groom"
                  onChange={e => setEditForm(f => ({ ...f, groomClientId: e.target.value }))}>
                  {users.filter(u => u.role?.name === 'groom_family').map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.email} ({u.role?.displayName || u.role?.name})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={editForm.status} label="Status" onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                {['draft','pending_confirmation','pending_approval','approved','contracted','rejected','inactive'].map(s => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Total Budget (₹)" type="number" fullWidth
              value={editForm.totalBudget} onChange={e => setEditForm(f => ({ ...f, totalBudget: e.target.value }))} />
            <TextField label="Event Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={editForm.eventDate} onChange={e => setEditForm(f => ({ ...f, eventDate: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Event Scale</InputLabel>
              <Select value={editForm.eventScale} label="Event Scale" onChange={e => setEditForm(f => ({ ...f, eventScale: e.target.value }))}>
                <MenuItem value="">Not set</MenuItem>
                {['intimate','small','medium','large','grand'].map(s => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Guest Count" type="number" fullWidth
              value={editForm.guestCount} onChange={e => setEditForm(f => ({ ...f, guestCount: e.target.value }))} />
            <TextField label="Notes" multiline rows={3} fullWidth
              value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, wishlist: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={updating}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Dialog ──────────────────────────────── */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, ids: [] })} maxWidth="sm" fullWidth>
        <DialogTitle>{rejectDialog.ids.length > 1 ? `Reject ${rejectDialog.ids.length} wishlists` : 'Reject Wishlist'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Provide a reason for rejection. The user will see this message.
          </Typography>
          <TextField
            label="Rejection Reason"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="e.g. Budget needs revision, missing details for venue..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, ids: [] })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={bulkBusy}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Detail View Dialog */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, wishlist: null })} maxWidth="md" fullWidth>
        <DialogTitle>Wishlist Details</DialogTitle>
        <DialogContent>
          {detailDialog.wishlist && (
            <Box>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>For:</strong> {detailDialog.wishlist.forWhom}</Typography>
                  <Typography variant="body2">
                    <strong>{detailDialog.wishlist.forWhom === 'both' ? 'Bride' : 'Client'}:</strong> {getUserName(detailDialog.wishlist)}
                  </Typography>
                  {detailDialog.wishlist.forWhom === 'both' && (
                    <Typography variant="body2"><strong>Groom:</strong> {getGroomName(detailDialog.wishlist) || 'Not set'}</Typography>
                  )}
                  <Typography variant="body2"><strong>Contractor:</strong> {getContractorName(detailDialog.wishlist)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Budget:</strong> ₹{Number(detailDialog.wishlist.totalBudget || 0).toLocaleString()}</Typography>
                  <Typography variant="body2"><strong>Event Date:</strong> {detailDialog.wishlist.eventDate || '—'}</Typography>
                  <Typography variant="body2"><strong>Guest Count:</strong> {detailDialog.wishlist.guestCount || '—'}</Typography>
                  <Typography variant="body2"><strong>Scale:</strong> {detailDialog.wishlist.eventScale || '—'}</Typography>
                  <Typography variant="body2"><strong>Function Place:</strong> {detailDialog.wishlist.eventCity || '—'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} mb={1}>Selected Services</Typography>
              {(detailDialog.wishlist.serviceSelections || []).map((cat, idx) => (
                <Paper key={idx} sx={{ p: 2, mb: 1.5, borderRadius: 2, bgcolor: '#fafafa' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    {cat.categoryName || cat.category}
                    {cat.serviceBudget > 0 && (
                      <Chip size="small" variant="outlined" color="primary" sx={{ ml: 1 }} label={`Budget ₹${Number(cat.serviceBudget).toLocaleString('en-IN')}`} />
                    )}
                  </Typography>
                  {(() => {
                    // Picked in the Service Wizard (automatically from keywords/budget/distance, or by the family)
                    const pv = (detailDialog.wishlist.preferredVendors || []).find(v => v.category === cat.category);
                    return pv && (
                      <Typography variant="body2" mb={1}>
                        <strong>Preferred vendor:</strong> {pv.businessName || pv.title}
                        {pv.businessName ? ` — ${pv.title}` : ''}{pv.packageName ? ` · ${pv.packageName}` : ''}{pv.city ? ` (${pv.city})` : ''}
                        {' '}<Chip size="small" variant="outlined" label={pv.auto ? 'Picked automatically' : 'Chosen by the family'} />
                        {!pv.available && <Chip size="small" color="warning" sx={{ ml: 0.5 }} label="No longer active" />}
                      </Typography>
                    );
                  })()}
                  {(cat.services || []).filter(s => s.selected).length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell align="right">Est. Budget</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(cat.services || []).filter(s => s.selected).map((svc, i) => (
                            <TableRow key={i}>
                              <TableCell>{svc.name}</TableCell>
                              <TableCell align="right">₹{Number(svc.budget || 0).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Category selected (no specific services)</Typography>
                  )}
                  {cat.answers && Object.keys(cat.answers).length > 0 && (
                    <Box mt={1}>
                      <Typography variant="caption" fontWeight={600}>Q&A Responses:</Typography>
                      {Object.entries(cat.answers).map(([key, val]) => (
                        <Typography key={key} variant="caption" display="block" color="text.secondary">
                          • {key}: {Array.isArray(val) ? val.join(', ') : val}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, wishlist: null })}>Close</Button>
          {detailDialog.wishlist && (
            <Button variant="outlined" startIcon={<EditIcon />}
              onClick={() => { openEdit(detailDialog.wishlist); setDetailDialog({ open: false, wishlist: null }); }}>
              Edit
            </Button>
          )}
          {detailDialog.wishlist?.status === 'pending_approval' && (
            <>
              <Button variant="contained" color="success" disabled={approving}
                onClick={() => { handleApprove(detailDialog.wishlist.id); setDetailDialog({ open: false, wishlist: null }); }}>
                Approve
              </Button>
              <Button variant="outlined" color="error"
                onClick={() => { setRejectDialog({ open: true, ids: [detailDialog.wishlist.id] }); setDetailDialog({ open: false, wishlist: null }); }}>
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
